import React, { useEffect, useState } from "react";
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  ArrowLeftIcon,
  FunnelIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ClockIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
} from "@mui/material";
import {
  getAllCandidates, getCandidatesByBranch, getProgramWiseCount,
  getProjectWiseCount, approveCandidate,
} from "../Api/candidateApi";
import {
  getProjectStats, getAllProjects, getProgramsByProject, getAllProgramsByProject,
  getDashboardData,
} from "../Api/projectApi";
import { getAllBranches } from "../Api/branches";
import { useNavigate } from "react-router-dom";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

const PROJECT_COLORS = {
  "Shriram Finance": { bg: "#fff7e0", border: "#FFCB09", accent: "#FFCB09", text: "#7a5c00" },
  Mahindra:         { bg: "#fff0f2", border: "#E31836", accent: "#E31836", text: "#8b0016" },
  Linde:            { bg: "#e8f0fb", border: "#002D54", accent: "#002D54", text: "#002D54" },
};
const DEFAULT_PROJECT_COLOR = { bg: "#f1f5f9", border: "#334155", accent: "#334155", text: "#1e293b" };
const PROJECT_NORMALIZE_MAP = {
  "Linde Hazmat": "Linde",
  "Linde Upskilling": "Linde",
  "SSSF Upskilling": "Shriram Finance",
};

const styles = `
  .dash-root { font-family:'DM Sans',sans-serif;  min-height:100vh; color:#1e293b; }
  .dash-root * { box-sizing:border-box; }

  .stat-card {
    position:relative; overflow:hidden; border-radius:22px; padding:28px 26px 22px;
    cursor:pointer; background:#fff; border:1.5px solid rgba(0,0,0,0.06);
    box-shadow:0 2px 16px rgba(0,0,0,0.06);
    transition:transform 0.28s cubic-bezier(.34,1.56,.64,1), box-shadow 0.25s ease;
  }
  .stat-card:hover { transform:translateY(-7px) scale(1.02); box-shadow:0 18px 44px rgba(0,0,0,0.11); }
  .stat-card .blob {
    position:absolute; width:140px; height:140px; border-radius:50%;
    filter:blur(40px); opacity:0.18; right:-22px; top:-22px; pointer-events:none;
  }
  .stat-card .icon-wrap {
    width:50px; height:50px; border-radius:14px;
    display:flex; align-items:center; justify-content:center; margin-bottom:18px;
  }
  .stat-card .s-label {
    font-size:11px; font-weight:600; letter-spacing:0.1em;
    text-transform:uppercase; color:#94a3b8; margin-bottom:5px;
  }
  .stat-card .s-value {
    font-family:'Syne',sans-serif; font-size:46px; font-weight:800;
    line-height:1; letter-spacing:-0.03em;
  }
  .stat-card .s-hint { margin-top:8px; font-size:12px; color:#cbd5e1; }

  .section-title { font-family:'Syne',sans-serif; font-size:24px; font-weight:800; color:#0f172a; margin-bottom:4px; }
  .section-sub   { font-size:13px; color:#94a3b8; margin-bottom:26px; }

  .chart-card {
    background:#fff; border:1.5px solid #e8edf5; border-radius:20px;
    padding:26px 24px 14px; box-shadow:0 2px 12px rgba(0,0,0,0.04);
  }
  .chart-title { font-family:'Syne',sans-serif; font-size:15px; font-weight:700; color:#0f172a; margin-bottom:2px; }
  .chart-sub   { font-size:12px; color:#94a3b8; margin-bottom:16px; }

  .project-card {
    border-radius:18px; padding:22px 20px; cursor:pointer;
    border:1.5px solid; position:relative; overflow:hidden;
    box-shadow:0 2px 10px rgba(0,0,0,0.05); display:flex; justify-content:space-between; align-items:center;
    transition:transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease;
  }
  .project-card:hover { transform:translateY(-5px); box-shadow:0 16px 38px rgba(0,0,0,0.11); }
  .project-card .proj-name { font-family:'Syne',sans-serif; font-weight:700; font-size:15px; }
  .project-card .proj-count { border-radius:50px; padding:5px 16px; font-family:'Syne',sans-serif; font-weight:700; font-size:14px; }

  .program-card {
    background:#fff; border:1.5px solid #e2e8f0; border-radius:14px;
    padding:18px; cursor:pointer; display:flex; justify-content:space-between; align-items:center;
    box-shadow:0 1px 6px rgba(0,0,0,0.04); transition:all 0.2s ease;
  }
  .program-card:hover { border-color:#6366f1; box-shadow:0 6px 24px rgba(99,102,241,0.12); transform:translateY(-2px); }
  .program-card .prog-name { font-weight:600; font-size:14px; color:#1e293b; }
  .program-card .prog-badge { background:#ede9fe; color:#6d28d9; border-radius:50px; padding:4px 13px; font-weight:700; font-size:12px; font-family:'Syne',sans-serif; }

  .dash-table { width:100%; border-collapse:separate; border-spacing:0; }
  .dash-table thead th {
    background:#f8fafc; color:#94a3b8; font-size:11px; font-weight:600;
    letter-spacing:0.1em; text-transform:uppercase; padding:14px 18px;
    border-bottom:1.5px solid #e8edf5;
  }
  .dash-table thead th:first-child { border-radius:14px 0 0 0; }
  .dash-table thead th:last-child  { border-radius:0 14px 0 0; }
  .dash-table tbody tr { transition:background 0.13s ease; cursor:pointer; }
  .dash-table tbody tr:hover td { background:#f8fafc; }
  .dash-table tbody td { padding:14px 18px; border-bottom:1px solid #f1f5f9; color:#334155; font-size:14px; background:#fff; }
  .dash-table tbody tr:last-child td { border-bottom:none; }
  .dash-table tbody tr:last-child td:first-child { border-radius:0 0 0 14px; }
  .dash-table tbody tr:last-child td:last-child  { border-radius:0 0 14px 0; }

  .badge { border-radius:50px; padding:4px 13px; font-size:11px; font-weight:700; letter-spacing:0.04em; }
  .badge-approved { background:#dcfce7; color:#16a34a; border:1px solid #bbf7d0; }
  .badge-rejected { background:#fee2e2; color:#dc2626; border:1px solid #fecaca; }
  .badge-pending  { background:#fef9c3; color:#ca8a04; border:1px solid #fde68a; }

  .filter-bar {
    background:#fff; border:1.5px solid #e2e8f0; border-radius:12px;
    padding:9px 14px; display:flex; align-items:center; gap:8px;
    color:#94a3b8; font-size:13px; transition:border-color 0.15s;
  }
  .filter-bar:focus-within { border-color:#6366f1; }
  .filter-bar input, .filter-bar select {
    background:transparent; border:none; outline:none;
    color:#1e293b; font-size:13px; flex:1; font-family:'DM Sans',sans-serif;
  }
  .filter-bar select option { background:#fff; }

  .back-btn {
    display:inline-flex; align-items:center; gap:8px;
    background:#fff; border:1.5px solid #e2e8f0; color:#64748b;
    padding:9px 18px; border-radius:10px; font-size:13px; font-weight:600;
    cursor:pointer; transition:all 0.15s ease; margin-bottom:28px;
    font-family:'DM Sans',sans-serif; box-shadow:0 1px 4px rgba(0,0,0,0.05);
  }
  .back-btn:hover { border-color:#6366f1; color:#6366f1; background:#f5f3ff; }

  .today-pill {
    background:linear-gradient(135deg,#22c55e,#16a34a); color:#fff;
    border-radius:50px; padding:7px 18px; font-size:13px; font-weight:700;
    font-family:'Syne',sans-serif; box-shadow:0 4px 12px rgba(34,197,94,0.25);
  }

  .mini-card {
    background:#fff; border:1.5px solid #e8edf5; border-radius:14px;
    padding:14px 18px; display:flex; align-items:center; gap:12px;
    box-shadow:0 1px 6px rgba(0,0,0,0.04);
  }

  .project-group { border-radius:20px; overflow:hidden; border:1.5px solid #e8edf5; margin-bottom:20px; box-shadow:0 2px 12px rgba(0,0,0,0.04); }
  .project-group-header { padding:18px 24px; font-family:'Syne',sans-serif; font-weight:700; font-size:15px; display:flex; align-items:center; gap:10px; }
  .project-group-body { background:#f8fafc; padding:18px 22px; display:grid; grid-template-columns:repeat(auto-fill,minmax(170px,1fr)); gap:10px; }
  .prog-chip { background:#fff; border:1.5px solid #e2e8f0; border-radius:10px; padding:12px 14px; font-size:13px; color:#475569; font-weight:500; transition:all 0.15s ease; }
  .prog-chip:hover { border-color:#6366f1; color:#4f46e5; background:#f5f3ff; }

  .skel { background:linear-gradient(90deg,#f1f5f9 25%,#e8edf5 50%,#f1f5f9 75%); background-size:200% 100%; animation:shimmer 1.5s infinite; border-radius:14px; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  .dash-root .MuiDialog-paper { background:#fff !important; color:#1e293b !important; border-radius:20px !important; box-shadow:0 24px 64px rgba(0,0,0,0.15) !important; }
  .dash-root .MuiDialogTitle-root { font-family:'Syne',sans-serif !important; font-weight:800 !important; color:#0f172a !important; border-bottom:1.5px solid #f1f5f9; font-size:18px !important; }
  .dash-root .MuiDialogActions-root { border-top:1.5px solid #f1f5f9; padding:14px 24px !important; }
  .dash-root .MuiButton-root { font-family:'DM Sans',sans-serif !important; text-transform:none !important; border-radius:10px !important; font-weight:600 !important; }
  .dash-root .MuiButton-contained { background:#6366f1 !important; color:#fff !important; box-shadow:0 4px 14px rgba(99,102,241,0.3) !important; }
  .dash-root .MuiButton-contained:hover { background:#4f46e5 !important; }
  .dash-root .MuiButton-text { color:#94a3b8 !important; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
  .anim-up { animation:fadeUp 0.32s ease both; }
  .anim-up:nth-child(2) { animation-delay:0.07s; }
  .anim-up:nth-child(3) { animation-delay:0.14s; }
`;

const Dashboard = () => {
  const [view, setView] = useState("dashboard");
  const [previousView, setPreviousView] = useState(null);
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  // ─── stat counts ───────────────────────────────────────────────────────────
  const [enrollCount, setEnrollCount]   = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [programCount, setProgramCount] = useState(0);
  const [loading, setLoading]           = useState(true);

  // ─── chart data ────────────────────────────────────────────────────────────
  const [locationChart, setLocationChart] = useState([]);
  const [projectChart, setProjectChart]   = useState([]);

  // ─── drill-down state ──────────────────────────────────────────────────────
  const [locationFilter, setLocationFilter]         = useState("");
  const [searchQuery, setSearchQuery]               = useState("");
  const [projects, setProjects]                     = useState([]);
  const [programs, setPrograms]                     = useState([]);
  const [selectedProject, setSelectedProject]       = useState("");
  const [selectedProgram, setSelectedProgram]       = useState(null);
  const [selectedBranch, setSelectedBranch]         = useState(null);
  const [centerCandidates, setCenterCandidates]     = useState([]);
  const [branchCounts, setBranchCounts]             = useState({});
  const [projectCounts, setProjectCounts]           = useState({});
  const [programCounts, setProgramCounts]           = useState({});
  const [selectedDate, setSelectedDate]             = useState("");
  const [todayCount, setTodayCount]                 = useState(0);
  const [allCenterCandidates, setAllCenterCandidates] = useState([]);
  const [loadingProjects, setLoadingProjects]       = useState(false);
  const [loadingLocations, setLoadingLocations]     = useState(false);
  const [approvalDialog, setApprovalDialog]         = useState(false);
  const [approvalStatus, setApprovalStatus]         = useState("");
  const [approvalReason, setApprovalReason]         = useState("");
  const [approvalCandidate, setApprovalCandidate]   = useState(null);
  const [allProgramsByProject, setAllProgramsByProject] = useState({});

  const changeView = (next) => {
    setPreviousView(view);
    setView(next);
    setSearchQuery("");
  };

  // ─── ✅ SINGLE API CALL for dashboard stats + charts ──────────────────────
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await getDashboardData();
      const d   = res.data.data;

      setEnrollCount(d.totalCandidates);
      setProjectCount(d.totalProjects);
      setProgramCount(d.totalPrograms);
      setLocationChart(d.locationChart);
      setProjectChart(d.projectChart);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // ─── drill-down helpers ────────────────────────────────────────────────────
  const handleProjectClick = async () => {
    try {
      setLoadingProjects(true);
      const res = await getAllProjects();
      setProjects([...new Set(res.data.map((p) => p.projectName))]);
      const cr  = await getProjectWiseCount();
      setProjectCounts(cr.data.data || {});
      changeView("projects");
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleOpenProject = async (project) => {
    setSelectedProject(project);
    setSelectedProgram(null);
    const res = await getProgramsByProject(project);
    setPrograms(res.data);
    const cr  = await getProgramWiseCount(project);
    setProgramCounts(cr?.data?.data || {});
    changeView("programs");
  };

  const handleProgramClick = async (program) => {
    try {
      setLoadingLocations(true);
      const res      = await getAllBranches();
      const filtered = res.data.data.filter(
        (b) => b.project === selectedProject && b.program === program
      );
      const results = await Promise.all(
        filtered.map(async (branch) => {
          const r    = await getCandidatesByBranch(branch._id, 1, 1000);
          const list = r.data.data || [];
          return {
            branchId: branch._id,
            total:    list.length,
            approved: list.filter((c) => c.approvalstatus === "Approved").length,
          };
        })
      );
      const counts = {};
      results.forEach((r) => {
        counts[r.branchId] = { total: r.total, approved: r.approved };
      });
      setBranchCounts(counts);
      setSelectedProgram({ name: program, branches: filtered });
      changeView("locations");
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleAllProgramsClick = async () => {
    const res = await getAllProgramsByProject();
    setAllProgramsByProject(res.data || {});
    changeView("allPrograms");
  };

  const handleCenterClick = async (branch) => {
    try {
      setSelectedBranch(branch);
      const res  = await getCandidatesByBranch(branch._id);
      const list = res.data.data || [];
      setAllCenterCandidates(list);
      setCenterCandidates(list);
      const today = new Date().toDateString();
      setTodayCount(list.filter((c) => new Date(c.createdAt).toDateString() === today).length);
      changeView("centerCandidates");
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!selectedDate) { setCenterCandidates(allCenterCandidates); return; }
    const picked = new Date(selectedDate).toDateString();
    setCenterCandidates(
      allCenterCandidates.filter((c) => new Date(c.createdAt).toDateString() === picked)
    );
  }, [selectedDate, allCenterCandidates]);

  const locationOptions = selectedProgram?.branches
    ? [...new Set(selectedProgram.branches.map((b) => b.location))]
    : [];

  const filteredBranches = (selectedProgram?.branches || []).filter((b) => {
    const locOk  = locationFilter ? b.location === locationFilter : true;
    const srchOk = searchQuery
      ? b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.location?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return locOk && srchOk;
  });

  const filteredCandidates = centerCandidates.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.contactNumber?.toLowerCase().includes(q) ||
      c.location?.toLowerCase().includes(q)
    );
  });

  const sortedLocations = [...locationChart].sort((a, b) => b.value - a.value).slice(0, 10);

  // ─── skeletons ─────────────────────────────────────────────────────────────
  const SkeletonCard    = () => <div className="skel" style={{ height: 130 }} />;
  const ChartSkeleton   = ({ height = 290 }) => <div className="skel" style={{ height }} />;

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>
      <div className="dash-root" style={{ padding: "32px 36px" }}>

        {view !== "dashboard" && (
          <button
            className="back-btn"
            onClick={() => {
              setView(previousView || "dashboard");
              setPreviousView(null);
              setSearchQuery("");
            }}
          >
            <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Back
          </button>
        )}

        {/* ══════════ DASHBOARD ══════════ */}
        {view === "dashboard" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 20, marginBottom: 28 }}>
              {loading ? (
                <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
              ) : (
                <>
                  <div className="stat-card anim-up" onClick={() => navigate("/candidates")}>
                    <div className="blob" style={{ background: "#38bdf8" }} />
                    <div className="icon-wrap" style={{ background: "#e0f2fe" }}>
                      <UserGroupIcon style={{ width: 24, height: 24, color: "#0284c7" }} />
                    </div>
                    <div className="s-label">Total Enrollments</div>
                    <div className="s-value" style={{ color: "#0284c7" }}>{enrollCount.toLocaleString()}</div>
                    <div className="s-hint">All registered candidates</div>
                  </div>

                  {role !== "branchUser" && (
                    <div className="stat-card anim-up" onClick={handleProjectClick}>
                      <div className="blob" style={{ background: "#818cf8" }} />
                      <div className="icon-wrap" style={{ background: "#ede9fe" }}>
                        <BuildingOfficeIcon style={{ width: 24, height: 24, color: "#6d28d9" }} />
                      </div>
                      <div className="s-label">Total Projects</div>
                      <div className="s-value" style={{ color: "#6d28d9" }}>{projectCount}</div>
                      <div className="s-hint">Active projects running</div>
                    </div>
                  )}

                  {role !== "branchUser" && (
                    <div className="stat-card anim-up" onClick={handleAllProgramsClick}>
                      <div className="blob" style={{ background: "#34d399" }} />
                      <div className="icon-wrap" style={{ background: "#dcfce7" }}>
                        <ClipboardDocumentListIcon style={{ width: 24, height: 24, color: "#059669" }} />
                      </div>
                      <div className="s-label">Total Programs</div>
                      <div className="s-value" style={{ color: "#059669" }}>{programCount}</div>
                      <div className="s-hint">Across all projects</div>
                    </div>
                  )}
                </>
              )}
            </div>

            {role !== "branchUser" && !loading && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 20 }}>
                <div className="chart-card">
                  <div className="chart-title">Location Wise Enrollments</div>
                  <div className="chart-sub">Candidate distribution across all centers</div>
                  {locationChart.length === 0 ? (
                    <ChartSkeleton />
                  ) : (
                    <BarChart
                      height={300}
                      margin={{ top: 20, bottom: 60, left: 50, right: 20 }}
                      sx={{
                        "& .MuiChartsAxis-tickLabel": { fill: "#64748b", fontSize: 12 },
                        "& .MuiChartsAxis-line":      { stroke: "#e8edf5" },
                        "& .MuiChartsAxis-tick":      { stroke: "#e8edf5" },
                        "& .MuiChartsLegend-root":    { display: "none" },
                      }}
                      xAxis={[{
                        scaleType: "band",
                        data: sortedLocations.map((d) => d.label),
                        tickLabelStyle: { angle: 0, fontSize: 12 },
                      }]}
                      series={[{
                        data:  sortedLocations.map((d) => d.value),
                        label: "Candidates",
                        color: "#6366f1",
                      }]}
                    />
                  )}
                </div>

                <div className="chart-card">
                  <div className="chart-title">Project Wise Enrollments</div>
                  <div className="chart-sub">Breakdown by project</div>
                  {projectChart.length === 0 ? (
                    <ChartSkeleton height={260} />
                  ) : (
                    <PieChart
                      height={260}
                      sx={{ "& .MuiChartsLegend-label": { fill: "#64748b", fontSize: 12 } }}
                      series={[{
                        data:         projectChart.map((d, i) => ({ id: i, value: d.value, label: d.label })),
                        innerRadius:  52,
                        paddingAngle: 3,
                        cornerRadius: 4,
                        colors:       ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444"],
                      }]}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════ PROJECTS ══════════ */}
        {view === "projects" && (
          <>
            <div className="section-title">Projects</div>
            <div className="section-sub">Select a project to explore its programs</div>
            {loadingProjects ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 16 }}>
                {[1, 2, 3].map((i) => <div key={i} className="skel" style={{ height: 80 }} />)}
              </div>
            ) : projects.length === 0 ? (
              <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 60 }}>No projects found</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 16 }}>
                {projects.map((project, idx) => {
                  const norm = PROJECT_NORMALIZE_MAP[project] || project;
                  const c    = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                  return (
                    <div
                      key={project}
                      className="project-card anim-up"
                      style={{ background: c.bg, borderColor: c.border, color: c.text, animationDelay: `${idx * 0.06}s` }}
                      onClick={() => handleOpenProject(project)}
                    >
                      <span className="proj-name">{project}</span>
                      <span className="proj-count" style={{ background: c.accent, color: "#fff" }}>
                        {projectCounts[norm] || 0}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ══════════ PROGRAMS ══════════ */}
        {view === "programs" && (
          <>
            <div className="section-title">{selectedProject}</div>
            <div className="section-sub">Choose a program to view its training centers</div>
            {programs.length === 0 ? (
              <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 60 }}>No programs available</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 14 }}>
                {programs.map((prog, idx) => (
                  <div
                    key={prog}
                    className="program-card anim-up"
                    style={{ animationDelay: `${idx * 0.06}s` }}
                    onClick={() => handleProgramClick(prog)}
                  >
                    <span className="prog-name">{prog}</span>
                    <span className="prog-badge">{programCounts[prog] || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ══════════ LOCATIONS ══════════ */}
        {view === "locations" && selectedProgram && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 22 }}>
              <div>
                <div className="section-title">{selectedProgram.name}</div>
                <div className="section-sub" style={{ marginBottom: 0 }}>Training centers</div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div className="filter-bar" style={{ width: 210 }}>
                  <MagnifyingGlassIcon style={{ width: 14, height: 14, flexShrink: 0, color: "#94a3b8" }} />
                  <input placeholder="Search centers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
                <div className="filter-bar" style={{ width: 170 }}>
                  <FunnelIcon style={{ width: 14, height: 14, flexShrink: 0, color: "#94a3b8" }} />
                  <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                    <option value="">All Locations</option>
                    {locationOptions.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {loadingLocations ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[1, 2, 3, 4].map((i) => <div key={i} className="skel" style={{ height: 52 }} />)}
              </div>
            ) : (
              <div style={{ borderRadius: 16, overflow: "hidden", border: "1.5px solid #e8edf5", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Center</th>
                      <th style={{ textAlign: "left" }}>Location</th>
                      <th style={{ textAlign: "center" }}>Enrolled</th>
                      <th style={{ textAlign: "center" }}>Approved</th>
                      <th style={{ textAlign: "center" }}>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBranches.map((branch) => {
                      const stats   = branchCounts[branch._id] || { total: 0, approved: 0 };
                      const pending = stats.total - stats.approved;
                      const pct     = stats.total ? Math.round((stats.approved / stats.total) * 100) : 0;
                      return (
                        <tr key={branch._id} onClick={() => handleCenterClick(branch)}>
                          <td style={{ fontWeight: 600, color: "#0f172a" }}>{branch.name}</td>
                          <td style={{ color: "#64748b" }}>{branch.location}</td>
                          <td style={{ textAlign: "center", fontWeight: 700, color: "#6366f1", fontFamily: "Syne,sans-serif" }}>{stats.total}</td>
                          <td style={{ textAlign: "center" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontWeight: 700, color: "#16a34a", fontFamily: "Syne,sans-serif" }}>{stats.approved}</span>
                              <span style={{ fontSize: 10, color: "#16a34a", background: "#dcfce7", borderRadius: 50, padding: "1px 7px" }}>{pct}%</span>
                            </span>
                          </td>
                          <td style={{ textAlign: "center", fontWeight: 700, color: "#ca8a04", fontFamily: "Syne,sans-serif" }}>{pending}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══════════ CENTER CANDIDATES ══════════ */}
        {view === "centerCandidates" && selectedBranch && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <div className="section-title">{selectedBranch.name}</div>
                <div className="section-sub" style={{ marginBottom: 0 }}>{selectedBranch.location} · {centerCandidates.length} candidates</div>
              </div>
              <div className="filter-bar" style={{ width: 210 }}>
                <MagnifyingGlassIcon style={{ width: 14, height: 14, flexShrink: 0, color: "#94a3b8" }} />
                <input placeholder="Search name, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <div className="filter-bar" style={{ width: 170 }}>
                <CalendarDaysIcon style={{ width: 14, height: 14, flexShrink: 0, color: "#94a3b8" }} />
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
              <div className="today-pill">Today: {todayCount}</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20, maxWidth: 460 }}>
              {[
                { label: "Total",    val: centerCandidates.length,                                                               color: "#6366f1", bg: "#ede9fe", Icon: UserGroupIcon },
                { label: "Approved", val: centerCandidates.filter((c) => c.approvalstatus === "Approved").length,                color: "#16a34a", bg: "#dcfce7", Icon: CheckBadgeIcon },
                { label: "Pending",  val: centerCandidates.filter((c) => !c.approvalstatus || c.approvalstatus === "Pending").length, color: "#ca8a04", bg: "#fef9c3", Icon: ClockIcon },
              ].map(({ label, val, color, bg, Icon }) => (
                <div key={label} className="mini-card">
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon style={{ width: 18, height: 18, color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                    <div style={{ fontFamily: "Syne,sans-serif", fontWeight: 800, fontSize: 22, color }}>{val}</div>
                  </div>
                </div>
              ))}
            </div>

            {filteredCandidates.length === 0 ? (
              <p style={{ color: "#94a3b8" }}>No candidates found</p>
            ) : (
              <div style={{ borderRadius: 16, overflow: "hidden", border: "1.5px solid #e8edf5", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Candidate</th>
                      <th style={{ textAlign: "left" }}>Contact</th>
                      <th style={{ textAlign: "left" }}>Location</th>
                      <th style={{ textAlign: "left" }}>Enrolled On</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                      {role === "admin" && <th style={{ textAlign: "center" }}>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.map((c) => (
                      <tr key={c._id}>
                        <td style={{ fontWeight: 600, color: "#0f172a" }}>{c.name}</td>
                        <td>{c.contactNumber}</td>
                        <td style={{ color: "#64748b" }}>{c.location}</td>
                        <td style={{ color: "#64748b" }}>{new Date(c.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                        <td style={{ textAlign: "center" }}>
                          <span className={`badge ${c.approvalstatus === "Approved" ? "badge-approved" : c.approvalstatus === "Rejected" ? "badge-rejected" : "badge-pending"}`}>
                            {c.approvalstatus === "Approved" ? "✓ Approved" : c.approvalstatus === "Rejected" ? "✗ Rejected" : "⏳ Pending"}
                          </span>
                        </td>
                        {role === "admin" && (
                          <td style={{ textAlign: "center" }}>
                            {c.approvalstatus === "Pending" && (
                              <button
                                onClick={() => { setApprovalCandidate(c); setApprovalStatus(""); setApprovalReason(""); setApprovalDialog(true); }}
                                style={{ background: "#ede9fe", border: "1.5px solid #c4b5fd", borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: "#6d28d9", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 5, transition: "all 0.15s ease", fontFamily: "DM Sans,sans-serif" }}
                                onMouseOver={(e) => e.currentTarget.style.background = "#ddd6fe"}
                                onMouseOut={(e)  => e.currentTarget.style.background = "#ede9fe"}
                              >
                                <CheckCircleIcon style={{ width: 14, height: 14 }} /> Review
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Review Candidate</DialogTitle>
              <DialogContent>
                <div style={{ paddingTop: 8 }}>
                  <div style={{ fontFamily: "Syne,sans-serif", fontWeight: 800, fontSize: 18, color: "#0f172a" }}>{approvalCandidate?.name}</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20 }}>{approvalCandidate?.contactNumber}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Select Action</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["Approved", "Rejected"].map((status) => (
                      <button
                        key={status}
                        onClick={() => setApprovalStatus(status)}
                        style={{ flex: 1, padding: 14, borderRadius: 12, cursor: "pointer", border: `2px solid ${approvalStatus === status ? (status === "Approved" ? "#16a34a" : "#dc2626") : "#e2e8f0"}`, background: approvalStatus === status ? (status === "Approved" ? "#dcfce7" : "#fee2e2") : "#f8fafc", color: approvalStatus === status ? (status === "Approved" ? "#16a34a" : "#dc2626") : "#94a3b8", fontWeight: 700, fontSize: 14, transition: "all 0.15s ease", fontFamily: "DM Sans,sans-serif" }}
                      >
                        {status === "Approved" ? "✓ Approve" : "✗ Reject"}
                      </button>
                    ))}
                  </div>
                  {approvalStatus === "Rejected" && (
                    <Box mt={2}>
                      <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Reason</div>
                      <textarea
                        value={approvalReason}
                        onChange={(e) => setApprovalReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        rows={3}
                        style={{ width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 13, resize: "vertical", outline: "none", fontFamily: "DM Sans,sans-serif", color: "#1e293b" }}
                      />
                    </Box>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!approvalStatus || (approvalStatus === "Rejected" && !approvalReason)}
                  onClick={async () => {
                    await approveCandidate(approvalCandidate._id, { approvalstatus: approvalStatus, approvalReason });
                    setApprovalDialog(false);
                    handleCenterClick(selectedBranch);
                  }}
                >
                  Submit Decision
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* ══════════ ALL PROGRAMS ══════════ */}
        {view === "allPrograms" && (
          <>
            <div className="section-title">All Programs</div>
            <div className="section-sub">Complete program listing by project</div>
            {Object.keys(allProgramsByProject).length === 0 ? (
              <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 60 }}>No programs found</p>
            ) : Object.keys(allProgramsByProject).map((project) => {
              const norm = PROJECT_NORMALIZE_MAP[project] || project;
              const c    = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
              return (
                <div key={project} className="project-group anim-up">
                  <div className="project-group-header" style={{ background: c.bg, color: c.text, borderBottom: `2px solid ${c.border}` }}>
                    <BuildingOfficeIcon style={{ width: 17, height: 17 }} />
                    {project}
                    <span style={{ marginLeft: "auto", fontSize: 12, background: c.accent, color: "#fff", borderRadius: 50, padding: "2px 12px" }}>
                      {allProgramsByProject[project].length}
                    </span>
                  </div>
                  <div className="project-group-body">
                    {allProgramsByProject[project].map((prog) => (
                      <div key={prog} className="prog-chip">{prog}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

      </div>
    </>
  );
};

export default Dashboard;